import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { BRAND, CONTACT } from "@/lib/brand";
import { Button } from "@/components/ui/button";

const navItems = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-md border-b border-slate-200"
          : "bg-white border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8 h-20 sm:h-24 flex items-center justify-between">
        <Link
          to="/"
          data-testid="navbar-logo-link"
          className="flex items-center gap-2"
        >
          <img
            src={BRAND.wordmark}
            alt="Clean Me — Cork commercial cleaning"
            className="h-16 sm:h-20 w-auto select-none"
            draggable="false"
          />
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-link-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `relative px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                  isActive
                    ? "text-slate-900"
                    : "text-slate-600 hover:text-slate-900"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {item.label}
                  {isActive && (
                    <span className="absolute left-1/2 -translate-x-1/2 -bottom-0.5 h-1 w-1 rounded-full bg-[#4CAF50]" />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href={`tel:${CONTACT.phoneTel}`}
            data-testid="navbar-phone-link"
            className="hidden lg:inline-flex items-center gap-2 text-sm text-slate-700 hover:text-slate-900 font-medium"
          >
            <Phone className="h-4 w-4 text-[#4CAF50]" />
            {CONTACT.phone}
          </a>
          <Link to="/contact">
            <Button
              data-testid="navbar-quote-btn"
              className="rounded-full h-11 px-6 bg-[#4CAF50] hover:bg-[#43A047] text-white btn-primary-glow font-semibold"
            >
              Get Quote
            </Button>
          </Link>
        </div>

        <button
          aria-label={open ? "Close menu" : "Open menu"}
          data-testid="navbar-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden inline-flex items-center justify-center h-11 w-11 rounded-full border border-slate-200 bg-white text-slate-900"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div
          data-testid="navbar-mobile-panel"
          className="md:hidden border-t border-slate-200 bg-white"
        >
          <div className="px-5 py-4 flex flex-col gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`mobile-nav-link-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-3 py-3 rounded-xl text-base font-medium ${
                    isActive
                      ? "bg-slate-100 text-slate-900"
                      : "text-slate-700 hover:bg-slate-50"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <a
              href={`tel:${CONTACT.phoneTel}`}
              data-testid="mobile-nav-phone"
              className="px-3 py-3 rounded-xl text-base font-medium text-slate-700 inline-flex items-center gap-2"
            >
              <Phone className="h-4 w-4 text-[#4CAF50]" />
              {CONTACT.phone}
            </a>
            <Link to="/contact" className="mt-2">
              <Button
                data-testid="mobile-nav-quote-btn"
                className="w-full rounded-full h-12 bg-[#4CAF50] hover:bg-[#43A047] text-white font-semibold"
              >
                Get Your Free Quote
              </Button>
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
